import type { ElementType, FormElement } from '../model/form.types';
import type {
  ElementDefinition,
  EngineElementType,
  FieldDefinition,
  HeaderDefinition,
  StaticDefinition,
} from '../model/types';
import { checkboxDefinition } from './definitions/checkbox';
import { dateInputDefinition } from './definitions/date-input';
import { dropdownDefinition } from './definitions/dropdown';
import { emailInputDefinition } from './definitions/email-input';
import { fillTitleDefinition } from './definitions/fill-title';
import { fixedTextDefinition } from './definitions/fixed-text';
import { headerDefinition } from './definitions/header';
import { imageDefinition } from './definitions/image';
import { indexerTitleDefinition } from './definitions/indexer-title';
import { numberInputDefinition } from './definitions/number-input';
import { paragraphDefinition } from './definitions/paragraph';
import { radioGroupDefinition } from './definitions/radio-group';
import { registerNumberInputDefinition } from './definitions/register-number-input';
import { tableDefinition } from './definitions/table';
import { textInputDefinition } from './definitions/text-input';
import { urlInputDefinition } from './definitions/url-input';

// Every EngineElementType must appear here. A type added to the union without an entry
// fails to compile, which is the contract this engine enforces between builder and fill.
export const ENGINE_DEFINITIONS = {
  HEADER: headerDefinition,
  FIXED_TEXT: fixedTextDefinition,
  IMAGE: imageDefinition,
  INDEXER_TITLE: indexerTitleDefinition,
  FILL_TITLE: fillTitleDefinition,
  TEXT_INPUT: textInputDefinition,
  PARAGRAPH: paragraphDefinition,
  EMAIL_INPUT: emailInputDefinition,
  URL_INPUT: urlInputDefinition,
  REGISTER_NUMBER_INPUT: registerNumberInputDefinition,
  NUMBER_INPUT: numberInputDefinition,
  DROPDOWN: dropdownDefinition,
  RADIO_GROUP: radioGroupDefinition,
  CHECKBOX: checkboxDefinition,
  DATE_INPUT: dateInputDefinition,
  TABLE: tableDefinition,
} satisfies Record<EngineElementType, ElementDefinition>;

export const ENGINE_ELEMENT_TYPES = Object.keys(ENGINE_DEFINITIONS) as EngineElementType[];

export function isEngineElementType(type: ElementType): type is EngineElementType {
  return type in ENGINE_DEFINITIONS;
}

export function getDefinition(element: FormElement): ElementDefinition | null {
  return isEngineElementType(element.type) ? ENGINE_DEFINITIONS[element.type] : null;
}

export function getFieldDefinition(element: FormElement): FieldDefinition | null {
  const definition = getDefinition(element);
  return definition?.kind === 'field' ? definition : null;
}

export function getStaticDefinition(element: FormElement): StaticDefinition | null {
  const definition = getDefinition(element);
  return definition?.kind === 'static' ? definition : null;
}

export function getHeaderDefinition(element: FormElement): HeaderDefinition | null {
  const definition = getDefinition(element);
  return definition?.kind === 'header' ? definition : null;
}
